import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Boxes, GitMerge, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PROVIDER_META } from "./providerMeta";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// Review-before-commit panel: parses an AI research answer into KG entities +
// relationships, lets the curator edit/deselect, then writes them to the graph.
export const ResearchExtractDialog = ({ open, onOpenChange, text, sources, provider, sourceLabel, onCommitted }) => {
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [rels, setRels] = useState([]);
  const [reference, setReference] = useState("");
  const [submit, setSubmit] = useState(false);
  const [committing, setCommitting] = useState(false);

  const defaultRef = useMemo(() => {
    const lines = (sources || [])
      .map((s) => `- ${s.title || s.url}${s.url && s.title ? ` (${s.url})` : ""}`)
      .join("\n");
    if (lines) return lines;
    if (sourceLabel) return sourceLabel;
    const providerName = PROVIDER_META[provider]?.name || "AI research";
    const today = new Date().toISOString().slice(0, 10);
    return `AI research (${providerName}, ${today})`;
  }, [sources, provider, sourceLabel]);

  useEffect(() => {
    if (!open) return;
    setEntities([]); setRels([]); setSubmit(false);
    setReference(defaultRef);
    setLoading(true);
    api.post("/ai/parse-research", { text, provider })
      .then((res) => {
        setEntities((res.data.entities || []).map((e) => ({ ...e, include: true })));
        setRels((res.data.relationships || []).map((r) => ({ ...r, include: true })));
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't parse the research");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameById = useMemo(() => {
    const m = {};
    entities.forEach((e) => { m[e.temp_id] = e.name; });
    return m;
  }, [entities]);

  const includedIds = useMemo(
    () => new Set(entities.filter((e) => e.include).map((e) => e.temp_id)),
    [entities],
  );

  const setEntity = (i, patch) =>
    setEntities((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const setEntityField = (i, key, value) =>
    setEntities((prev) => prev.map((e, idx) => (idx === i ? { ...e, fields: { ...e.fields, [key]: value } } : e)));
  const setRel = (i, patch) =>
    setRels((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const chosenCount = entities.filter((e) => e.include).length;

  const commit = async () => {
    const chosen = entities.filter((e) => e.include);
    if (chosen.length === 0) { toast.error("Select at least one entity"); return; }
    const chosenRels = rels.filter(
      (r) => r.include && includedIds.has(r.source_temp_id) && includedIds.has(r.target_temp_id));
    setCommitting(true);
    try {
      const res = await api.post("/ai/commit-research", {
        entities: chosen.map((e) => ({
          temp_id: e.temp_id, type: e.type, name: e.name, fields: e.fields, reference,
        })),
        relationships: chosenRels.map((r) => ({
          source_temp_id: r.source_temp_id, target_temp_id: r.target_temp_id,
          type: r.type, props: r.props, reference,
        })),
        submit,
      });
      const ne = res.data.created_entities.length;
      const nr = res.data.created_relationships.length;
      toast.success(`Created ${ne} ${ne === 1 ? "entity" : "entities"}${nr ? ` and ${nr} relationship${nr === 1 ? "" : "s"}` : ""}${submit ? " (submitted for review)" : " (as drafts)"}`);
      onOpenChange(false);
      onCommitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create entities");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col" data-testid="research-extract-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Review extracted entities
          </DialogTitle>
          <DialogDescription>
            The AI parsed this research into entities and relationships. Edit or uncheck anything, then create them in the graph.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground" data-testid="extract-loading">
            <Loader2 className="w-7 h-7 animate-spin text-primary" />
            <p className="text-sm">Parsing the research into entities…</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-1 space-y-5">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                <Boxes className="w-4 h-4 text-primary" /> Entities ({chosenCount}/{entities.length})
              </div>
              <div className="space-y-3">
                {entities.map((e, i) => (
                  <div key={e.temp_id} className={`rounded-lg border p-3 transition-colors ${e.include ? "border-primary/40 bg-secondary/30" : "border-border opacity-60"}`}
                    data-testid={`extract-entity-${e.temp_id}`}>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={e.include} onCheckedChange={(v) => setEntity(i, { include: !!v })}
                        data-testid={`extract-entity-include-${e.temp_id}`} />
                      <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">
                        {e.type_label || e.type}
                      </span>
                      <Input value={e.name} onChange={(ev) => setEntity(i, { name: ev.target.value })}
                        className="h-8 text-sm font-medium" data-testid={`extract-entity-name-${e.temp_id}`} />
                    </div>
                    {Object.keys(e.fields || {}).length > 0 && e.include && (
                      <div className="mt-2 space-y-2 pl-6">
                        {Object.entries(e.fields).map(([key, val]) => (
                          <div key={key}>
                            <Label className="text-[11px] text-muted-foreground">{key}</Label>
                            <Textarea value={val} onChange={(ev) => setEntityField(i, key, ev.target.value)}
                              className="min-h-[52px] text-xs mt-0.5" data-testid={`extract-field-${e.temp_id}-${key}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {rels.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                  <GitMerge className="w-4 h-4 text-primary" /> Relationships
                </div>
                <div className="space-y-2">
                  {rels.map((r, i) => {
                    const ok = includedIds.has(r.source_temp_id) && includedIds.has(r.target_temp_id);
                    return (
                      <label key={`${r.source_temp_id}-${r.type}-${r.target_temp_id}`} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 ${ok ? "border-border" : "border-border opacity-40"}`}
                        data-testid={`extract-rel-${i}`}>
                        <Checkbox checked={r.include && ok} disabled={!ok}
                          onCheckedChange={(v) => setRel(i, { include: !!v })}
                          data-testid={`extract-rel-include-${i}`} />
                        <span className="truncate">
                          <b className="text-foreground">{nameById[r.source_temp_id]}</b>
                          <span className="text-primary mx-1.5">{r.label || r.type}</span>
                          <b className="text-foreground">{nameById[r.target_temp_id]}</b>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold text-foreground">Source / reference (added to every item)</Label>
              <Textarea value={reference} onChange={(e) => setReference(e.target.value)}
                className="min-h-[64px] text-xs mt-1" data-testid="extract-reference" />
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-border pt-3 flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Switch id="extract-submit" checked={submit} onCheckedChange={setSubmit} data-testid="extract-submit-toggle" />
            <Label htmlFor="extract-submit" className="text-sm text-muted-foreground">Submit for review (else save as drafts)</Label>
          </div>
          <Button onClick={commit} disabled={loading || committing || chosenCount === 0}
            className="bg-primary hover:bg-primary/90" data-testid="extract-commit-btn">
            {committing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Create {chosenCount} {chosenCount === 1 ? "entity" : "entities"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
